import { talk } from "../pr_utils";
import { useEffect, useState, useCallback } from "react";
import Styles from "../styles/devotional.module.css";

const VERSION_KEY = "devoVersion"; // localStorage key
const isValidVersion = (v) => v === "modern" || v === "original";

// Helper to read devo-related params from URL
const getDevoQueryParams = () => {
    try {
        const params = new URLSearchParams(window.location.search);

        const monthParam = params.get("month");
        const dateParam = params.get("date");
        const timeParam = params.get("time"); // "morning" | "evening" | null

        const month = parseInt(monthParam, 10);
        const date = parseInt(dateParam, 10);

        const safeMonth =
            Number.isInteger(month) && month >= 1 && month <= 12 ? month : null;
        const safeDate =
            Number.isInteger(date) && date >= 1 && date <= 31 ? date : null;

        let forMorning = null;
        if (timeParam) {
            const t = timeParam.toLowerCase();
            if (t === "morning") forMorning = 1;
            else if (t === "evening") forMorning = 0;
        }

        return { month: safeMonth, date: safeDate, forMorning };
    } catch {
        return { month: null, date: null, forMorning: null };
    }
};

const MorningAndEvening = () => {
    const [devoData, setDevoData] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [version, setVersion] = useState("modern"); // 'modern' | 'original'

    // Resolve initial version: URL > localStorage > default('modern')
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const urlVersion = params.get("version");
            if (isValidVersion(urlVersion)) {
                setVersion(urlVersion);
                // also persist if user arrives via a sharable link
                localStorage.setItem(VERSION_KEY, urlVersion);
                return;
            }
            const saved = localStorage.getItem(VERSION_KEY);
            if (isValidVersion(saved)) {
                setVersion(saved);
            }
        } catch {
            // ignore and keep default
        }
    }, []);

    // Load devotional once on mount, using optional URL params
    useEffect(() => {
        loadDevotional();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadDevotional = () => {
        const { month, date, forMorning } = getDevoQueryParams();

        const payload = {};
        if (month !== null) payload.month = month;
        if (date !== null) payload.date = date;
        if (forMorning !== null) payload.for_morning = forMorning;

        talk(
            "handlers/DevotionalHandler.php",
            "GET_MORNING_AND_EVENING",
            payload,
            loadDevotionalHandler
        );
    };

    const loadDevotionalHandler = (operation, sentData, returnData) => {
        if (returnData.success) {
            setDevoData(returnData.data);
            setErrorMessage("");
        } else {
            setDevoData(false);
            setErrorMessage("Failed to load devotional.");
        }
    };

    const getMonthName = useCallback((monthNumber) => {
        const months = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];
        return months[monthNumber - 1] || null;
    }, []);

    // Toggle handlers (click + keyboard)
    const handleSelect = (v) => {
        if (!isValidVersion(v)) return;
        setVersion(v);
        try {
            localStorage.setItem(VERSION_KEY, v);
        } catch {}
    };

    const onKeyDownTab = (e) => {
        if (!["ArrowLeft", "ArrowRight"].includes(e.key)) return;
        e.preventDefault();
        const next = version === "modern" ? "original" : "modern";
        handleSelect(next);
    };

    const bodyHtml =
        version === "modern"
            ? devoData?.modern_english
            : devoData?.body;

    return (
        <div className={Styles.devoContainer}>

            {/* Version Toggle */}
            <div
                className={Styles.versionToggle}
                role="tablist"
                aria-label="Select devotional version"
                onKeyDown={onKeyDownTab}
            >
                <button
                    type="button"
                    role="tab"
                    aria-selected={version === "modern"}
                    tabIndex={version === "modern" ? 0 : -1}
                    className={`${Styles.versionTab} ${
                        version === "modern" ? Styles.versionTabActive : ""
                    }`}
                    onClick={() => handleSelect("modern")}
                >
                    Modern English
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={version === "original"}
                    tabIndex={version === "original" ? 0 : -1}
                    className={`${Styles.versionTab} ${
                        version === "original" ? Styles.versionTabActive : ""
                    }`}
                    onClick={() => handleSelect("original")}
                >
                    Original Text
                </button>
            </div>

            <div className={Styles.devoHeader}>Morning and Evening Devotional</div>

            {devoData && (
                <div className={Styles.devoDate}>
                    {getMonthName(devoData.month)} {devoData.date} —{" "}
                    {devoData.for_morning ? "Morning" : "Evening"}
                </div>
            )}

            <div className={Styles.devoAuthor}>by Charles H. Spurgeon</div>

            {errorMessage ? (
                <div className={Styles.devoError}>{errorMessage}</div>
            ) : null}

            {devoData ? (
                <div className={Styles.devoContent}>
                    <div className={Styles.devoVerse}>
                        <span className={Styles.devoVerseText}>
                            {devoData.opening_verse_text}
                        </span>{" "}
                        <span className={Styles.devoVerseRef}>
                            {devoData.opening_verse_reference}
                        </span>
                    </div>

                    <div
                        className={Styles.devoBody}
                        dangerouslySetInnerHTML={{ __html: bodyHtml }}
                    />
                </div>
            ) : null}

            <div className={Styles.licenseContainer}>
                <a href="https://heartlandaoc.com/morning_and_evening">
                    Spurgeon's Morning and Evening in Modern English
                </a>{" "}
                © 2025 by{" "}
                <a href="https://example.com">Michael Turner</a> is licensed under{" "}
                <a href="https://creativecommons.org/licenses/by-nc-nd/4.0/">
                    CC BY-NC-ND 4.0
                </a>
                <img
                    src="https://mirrors.creativecommons.org/presskit/icons/cc.svg"
                    alt=""
                    className={Styles.ccIcon}
                />
                <img
                    src="https://mirrors.creativecommons.org/presskit/icons/by.svg"
                    alt=""
                    className={Styles.ccIcon}
                />
                <img
                    src="https://mirrors.creativecommons.org/presskit/icons/nc.svg"
                    alt=""
                    className={Styles.ccIcon}
                />
                <img
                    src="https://mirrors.creativecommons.org/presskit/icons/nd.svg"
                    alt=""
                    className={Styles.ccIcon}
                />
            </div>

        </div>
    );
};

export default MorningAndEvening;
